import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { SvgArrowRight, SvgStarFilled } from '@coursera/coursera-ui/svg';

import type { CareerPlansCourseCardType } from 'bundles/career-plans/types/career-plans-course-card';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { getS12nVariantLabel } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/career-plans';

import 'css!./__styles__/CareerPlansCourseCard';

type Props = CareerPlansCourseCardType;

const careerPlansCourseCard = ({
  id,
  careerName,
  name,
  description,
  descriptionOverride,
  imageUrl,
  partners = [],
  averageFiveStarRating,
  ratingCount,
  type,
  objectUrl,
  courseCount,
  isCourse,
  isProject,
  s12nProductVariant,
  isFoundational = false,
  loaded = false,
  positionIndex,
  careerPosition,
  computedLevel,
}: Props) => {
  if (!loaded || !name || !objectUrl) return null;

  /*
    computedType and computedDescription are being built here due to the necessity for supporting both 
    static and dynamic content. 
    
    With static content, we're free to build out descriptions however we like, but we're limited to what's 
    returned from our APIs with dynamic content without an intentional alternative. descriptionOverride is
    populated by users on contentful.

    Types are explicitly stated due to how we built out contentful models, so it seems easier to pull from 
    that than to destructure a response.
  */
  let computedType = type;
  if (isProject) {
    computedType = _t('Guided Project');
  } else if (s12nProductVariant) {
    computedType = getS12nVariantLabel(s12nProductVariant);
  } else if (isCourse) {
    computedType = _t('Course');
  }

  const computedDescription = descriptionOverride?.length ? descriptionOverride : description;
  let altTagContent = name;
  let localizedByPartner;

  if (partners?.length > 0) {
    localizedByPartner = _t('by #{partner}', {
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      partner: partners[0].name,
    });
    altTagContent = _t('#{name} by #{partner}', {
      name,
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      partner: partners[0].name,
    });
  }

  const careerPlansCourseCardAriaLabel = _t(
    'Go to #{type}: #{productName} #{localizedByPartner}. Star ratings: #{averageFiveStarRating} out of 5. Rating count: #{ratingCount}. ',
    {
      type: computedType,
      productName: name,
      localizedByPartner,
      averageFiveStarRating: averageFiveStarRating?.toLocaleString(),
      ratingCount: ratingCount?.toLocaleString(),
    }
  );

  const CareerPlansCourseCardImage = () => {
    const ImageComponent = (
      <Imgix
        src={imageUrl}
        alt={altTagContent.toLocaleString()}
        maxWidth={100}
        maxHeight={100}
        imgParams={{ fit: 'fill', auto: 'format,compress', bg: 'FFF' }}
        className="careerPlansCourseCard-image"
      />
    );

    return <div className="careerPlansCourseCard-imageContainer">{ImageComponent}</div>;
  };

  const CareerPlansCourseCardLink = () => (
    <TrackedA
      withVisibilityTracking={true}
      requireFullyVisible={false}
      trackingName="careerplans_course_link"
      className="careerPlansCourseCard-link"
      href={objectUrl}
      data={{
        entityid: id,
        entityindex: positionIndex,
        listidentifier: careerName,
        listindex: careerPosition,
        subtype: computedLevel,
      }}
      aria-hidden={true}
      tabIndex={-1}
    >
      {_t('Go to #{type}', { type: computedType })}
      <SvgArrowRight size={18} color="var(--cds-color-blue-600)" hoverColor="var(--cds-color-blue-700)" />
    </TrackedA>
  );

  const CareerPlansCourseCardTypeText = () => (
    <div className="careerPlansCourseCard-type careerPlans-sectionHeader">
      {computedType}
      {courseCount && courseCount > 1 && ` (${courseCount} Courses)`}
    </div>
  );

  const CareerPlansCourseCardRating = () => (
    <div className="careerPlansCourseCard-metaData--ratings">
      <span className="careerPlansCourseCard-metaData--star">
        <SvgStarFilled size={14} color="var(--cds-color-yellow-200)" stroke="var(--cds-color-yellow-900)" />
      </span>
      <span className="careerPlansCourseCard-metaData--rating">{averageFiveStarRating}</span>
      {!!ratingCount && (
        <span className="careerPlansCourseCard-metaData--ratingCount" data-e2e="CareerPlansProductCard-rating--count">
          {_t('#{ratingCount} Ratings', { ratingCount: ratingCount?.toLocaleString() })}
        </span>
      )}
    </div>
  );

  const cardButtonTrackingName = `careerplans_course_card`;
  const cardButtonId = `${cardButtonTrackingName}~${careerName}~${name.replace(/\s+/g, '_')}`;

  return (
    <section className="rc-CareerPlansCourseCard" data-e2e="CareerPlansProductCard">
      <TrackedA
        id={cardButtonId}
        trackingName={cardButtonTrackingName}
        className="careerPlansCourseCard-button"
        href={objectUrl}
        data={{
          entityid: id,
          entityindex: positionIndex,
          listidentifier: careerName,
          listindex: careerPosition,
          subtype: computedLevel,
        }}
        withVisibilityTracking={true}
        requireFullyVisible={false}
        role="link"
        ariaLabel={careerPlansCourseCardAriaLabel + computedDescription}
        target="_blank"
      >
        <div className="careerCard-container is-bottom">
          <div className="careerCard-container--left">
            {imageUrl && <CareerPlansCourseCardImage data-e2e="CareerPlansProductCard-image" />}
          </div>
          <div className="careerCard-container--right">
            <div className="careerCard-container--right_text">
              <div className="careerPlansCourseCard-name" data-e2e="CareerPlansProductCard-name">
                {name}
                {isFoundational && (
                  <span className="careerPlansCourseCard-name--tag" data-e2e="CareerPlansProductCard-foundationalTag">
                    {_t('Foundational Learning')}
                  </span>
                )}
              </div>
              <div className="careerPlansCourseCard-metaData">
                {computedType && <CareerPlansCourseCardTypeText data-e2e="CareerPlansProductCard-type" />}
                {averageFiveStarRating && <CareerPlansCourseCardRating data-e2e="CareerPlansProductCard-rating" />}
              </div>
              {computedDescription && (
                <div
                  className="careerPlansCourseCard-description careerPlans-text"
                  data-e2e="CareerPlansProductCard-description"
                >
                  {computedDescription}
                </div>
              )}
              {objectUrl && computedType && <CareerPlansCourseCardLink data-e2e="CareerPlansProductCard-link" />}
            </div>
          </div>
        </div>
      </TrackedA>
    </section>
  );
};

export default careerPlansCourseCard;
